import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { trackGA } from "@/lib/analytics";
import { CheckCircle2, Loader2, AlertTriangle, Phone, Clock, Shield } from "lucide-react";
import { Logo } from "@/components/Logo";

/**
 * Public landing page hit from the homeowner confirmation email CTA.
 * Auto-confirms "Ready to Book" the moment the page paints, then shows
 * a friendly confirmation so the homeowner knows their local pro has
 * been alerted.
 */
export default function ReadyToBook() {
  const { leadId } = useParams();
  const fired = useRef(false);
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    api.post(`/public/leads/${leadId}/ready-to-book`)
      .then((r) => {
        setState({ loading: false, data: r.data, error: null });
        trackGA("ready_to_book_clicked", {}, { once: true });
      })
      .catch((e) => {
        setState({
          loading: false,
          data: null,
          error: formatApiErrorDetail(e.response?.data?.detail) || "We couldn't find that quote. Reply to your quote email and we'll sort it out.",
        });
      });
  }, [leadId]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-[#FBFAF6] flex items-center justify-center" data-testid="ready-loading">
        <div className="text-slate-500 inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Notifying your local pro…
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen bg-[#FBFAF6]">
        <Nav />
        <main className="max-w-2xl mx-auto px-6 py-16 sm:py-24 text-center" data-testid="ready-error">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-100 text-amber-600 mb-5">
            <AlertTriangle className="w-7 h-7" />
          </div>
          <h1 className="text-3xl font-display font-bold text-slate-900 mb-2">Hmm — that link didn't work</h1>
          <p className="text-slate-600 mb-8">{state.error}</p>
          <Link to="/" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#C75D33] text-white font-semibold hover:bg-[#A14828] transition">
            Get a new quote →
          </Link>
        </main>
      </div>
    );
  }

  const first = state.data?.first_name || "there";
  const zip = state.data?.zip_code;

  return (
    <div className="min-h-screen bg-[#FBFAF6]">
      <Nav />
      <main className="max-w-3xl mx-auto px-6 py-12 sm:py-20" data-testid="ready-confirmed">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 mb-5">
            <CheckCircle2 className="w-9 h-9" strokeWidth={2.5} />
          </div>
          <div className="inline-block text-[11px] font-bold uppercase tracking-[0.18em] text-[#C75D33] bg-[#FFF6F0] border border-[#FBD9C7] rounded-full px-3 py-1 mb-4">
            You're booked in
          </div>
          <h1 className="text-3xl sm:text-5xl font-display font-bold text-slate-900 mb-3 leading-tight">
            Nice work, {first} — your local pro has been alerted.
          </h1>
          <p className="text-base sm:text-lg text-slate-600 max-w-xl mx-auto">
            Expect a call from a screen pro {zip ? <>serving <span className="font-semibold text-slate-900">{zip}</span></> : "in your area"} shortly to lock in your install window.
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6 sm:p-8 mb-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-4">
            What happens next
          </div>
          <ul className="space-y-5">
            <Step
              icon={Phone}
              title="A pro will call today"
              desc="Usually within an hour during business hours. They'll confirm your address and the screens to be measured."
            />
            <Step
              icon={Clock}
              title="On-site measure + final quote"
              desc="The pro measures in person and confirms the price — your quote total stays as the cap unless you add work."
            />
            <Step
              icon={Shield}
              title="Install on a day that works for you"
              desc="Most jobs are installed the same week. You only pay after the work is complete."
            />
          </ul>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5 sm:p-6 text-center text-sm text-slate-600">
          Didn't hear back? Just reply to your quote email — we'll get a different pro on it.
        </div>
      </main>
    </div>
  );
}

function Nav() {
  return (
    <nav className="px-5 sm:px-8 lg:px-12 py-4 border-b border-slate-200/60 bg-white/80 backdrop-blur sticky top-0 z-40">
      <div className="max-w-5xl mx-auto flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="ready-home-link">
          <Logo size={36} withWordmark />
        </Link>
        <Link to="/contact" className="text-sm font-semibold text-slate-600 hover:text-slate-900" data-testid="ready-contact-link">
          Need help?
        </Link>
      </div>
    </nav>
  );
}

function Step({ icon: Icon, title, desc }) {
  return (
    <li className="flex gap-4">
      <div className="w-10 h-10 shrink-0 rounded-xl bg-[#C75D33]/10 text-[#C75D33] flex items-center justify-center">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="font-bold text-slate-900">{title}</div>
        <div className="text-sm text-slate-600 leading-relaxed mt-0.5">{desc}</div>
      </div>
    </li>
  );
}
